import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { db, COLLECTIONS } from './firebase';
import type {
  TrainingHost,
  TrainingHostInput,
  TrainingProgram,
  TrainingProgramInput,
} from '../types/trainingHost';
import { TRAINING_HOST_RESERVED_SLUGS } from '../config/contentRoutes';
import { DEFAULT_TRAINING_CONTENT_HTML, sanitizeTrainingHtml } from './trainingHtml';

const hostsCol = () => collection(db, COLLECTIONS.TRAINING_HOSTS);
const programsCol = () => collection(db, COLLECTIONS.TRAINING_PROGRAMS);
const slugDocRef = (slug: string) => doc(db, COLLECTIONS.TRAINING_HOST_SLUGS, slug);

const toDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
};

const mapHost = (id: string, data: Record<string, unknown>): TrainingHost => ({
  id,
  ownerUid: String(data.ownerUid ?? id),
  publicSlug: String(data.publicSlug ?? ''),
  displayName: String(data.displayName ?? ''),
  bio: data.bio ? String(data.bio) : undefined,
  schoolName: data.schoolName ? String(data.schoolName) : undefined,
  photoURL: data.photoURL ? String(data.photoURL) : undefined,
  isPublic: data.isPublic !== false,
  createdAt: toDate(data.createdAt),
  updatedAt: toDate(data.updatedAt),
});

const mapProgram = (id: string, data: Record<string, unknown>): TrainingProgram => ({
  id,
  hostId: String(data.hostId ?? ''),
  ownerUid: String(data.ownerUid ?? ''),
  title: String(data.title ?? ''),
  description: data.description ? String(data.description) : undefined,
  status: (data.status as TrainingProgram['status']) ?? 'upcoming',
  date: data.date ? String(data.date) : undefined,
  category: data.category ? String(data.category) : undefined,
  tags: Array.isArray(data.tags) ? data.tags.map(String) : undefined,
  programSlug: data.programSlug ? String(data.programSlug) : undefined,
  contentType:
    data.contentType === 'legacy'
      ? 'legacy'
      : data.contentType === 'editor' || data.contentHtml
        ? 'editor'
        : data.path
          ? 'legacy'
          : undefined,
  contentHtml: data.contentHtml ? String(data.contentHtml) : undefined,
  path: data.path ? String(data.path) : undefined,
  linkType: data.linkType === 'external' ? 'external' : 'internal',
  isPublic: data.isPublic !== false,
  sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
  createdAt: toDate(data.createdAt),
  updatedAt: toDate(data.updatedAt),
});

const parseProgramDate = (date?: string): number => {
  if (!date) return 0;
  const parsed = Date.parse(date);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** 연수 목록: date 최신순, 날짜 없으면 updatedAt·createdAt 기준 */
export const sortTrainingProgramsByDateDesc = (programs: TrainingProgram[]): TrainingProgram[] =>
  [...programs].sort((a, b) => {
    const dateDiff = parseProgramDate(b.date) - parseProgramDate(a.date);
    if (dateDiff !== 0) return dateDiff;

    const updatedA = a.updatedAt?.getTime() ?? a.createdAt?.getTime() ?? 0;
    const updatedB = b.updatedAt?.getTime() ?? b.createdAt?.getTime() ?? 0;
    return updatedB - updatedA;
  });

export const normalizeTrainingHostSlug = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9가-힣-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const PROGRAM_SLUG_RESERVED = ['p', 'programs', 'new', 'edit'];

export const isValidTrainingHostSlug = (slug: string): boolean => {
  if (slug.length < 2 || slug.length > 40) return false;
  if ((TRAINING_HOST_RESERVED_SLUGS as readonly string[]).includes(slug)) return false;
  return /^[a-z0-9가-힣]([a-z0-9가-힣-]*[a-z0-9가-힣])?$/.test(slug);
};

export const normalizeProgramSlug = normalizeTrainingHostSlug;

export const isValidProgramSlug = (slug: string): boolean => {
  if (!isValidTrainingHostSlug(slug)) return false;
  return !PROGRAM_SLUG_RESERVED.includes(slug);
};

export function suggestProgramSlugFromTitle(title: string): string {
  const base = normalizeProgramSlug(title) || `training-${Date.now().toString(36)}`;
  return base.slice(0, 40);
}

/** 공개 허브 조회 (비로그인·타인). 규칙과 맞추기 위해 isPublic=true 조건 포함 */
export async function getPublicTrainingHostBySlug(slug: string): Promise<TrainingHost | null> {
  const q = query(hostsCol(), where('publicSlug', '==', slug), where('isPublic', '==', true));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return mapHost(docSnap.id, docSnap.data() as Record<string, unknown>);
}

/** slug로 허브 조회. 본인 비공개 허브는 viewerUid로 소유자일 때만 반환 */
export async function getTrainingHostBySlug(slug: string, viewerUid?: string): Promise<TrainingHost | null> {
  const publicHost = await getPublicTrainingHostBySlug(slug);
  if (publicHost) return publicHost;

  if (!viewerUid) return null;

  const ownHost = await getTrainingHostByOwnerUid(viewerUid);
  if (ownHost?.publicSlug === slug) return ownHost;
  return null;
}

export async function getTrainingHostByOwnerUid(ownerUid: string): Promise<TrainingHost | null> {
  const ref = doc(db, COLLECTIONS.TRAINING_HOSTS, ownerUid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return mapHost(snap.id, snap.data() as Record<string, unknown>);
}

export function subscribePublicProgramCountByHost(
  onData: (counts: Record<string, number>) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(programsCol(), where('isPublic', '==', true));
  return onSnapshot(
    q,
    (snap) => {
      const counts: Record<string, number> = {};
      snap.docs.forEach((d) => {
        const hostId = String(d.data().hostId ?? '');
        if (hostId) counts[hostId] = (counts[hostId] ?? 0) + 1;
      });
      onData(counts);
    },
    (err) => onError?.(err)
  );
}

export function subscribePublicTrainingHosts(
  onData: (hosts: TrainingHost[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(hostsCol(), where('isPublic', '==', true), orderBy('displayName', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => mapHost(d.id, d.data() as Record<string, unknown>)));
    },
    (err) => onError?.(err)
  );
}

export function subscribeTrainingProgramsByHost(
  hostId: string,
  options: { includePrivate?: boolean },
  onData: (programs: TrainingProgram[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  // includePrivate: 규칙과 맞추려 ownerUid 조건 필요 (hostId === ownerUid)
  const q = options.includePrivate
    ? query(programsCol(), where('ownerUid', '==', hostId), orderBy('sortOrder', 'asc'))
    : query(
        programsCol(),
        where('hostId', '==', hostId),
        where('isPublic', '==', true),
        orderBy('sortOrder', 'asc')
      );

  return onSnapshot(
    q,
    (snap) => {
      const programs = snap.docs.map((d) => mapProgram(d.id, d.data() as Record<string, unknown>));
      onData(sortTrainingProgramsByDateDesc(programs));
    },
    (err) => onError?.(err)
  );
}

async function assertSlugAvailable(slug: string, ownerUid: string): Promise<string | null> {
  const slugSnap = await getDoc(slugDocRef(slug));
  if (!slugSnap.exists()) return null;
  const existingOwner = String(slugSnap.data()?.ownerUid ?? '');
  if (existingOwner && existingOwner !== ownerUid) {
    throw new Error('이미 사용 중인 주소입니다. 다른 주소를 입력해 주세요.');
  }
  return existingOwner === ownerUid ? slug : null;
}

export async function upsertTrainingHost(ownerUid: string, input: TrainingHostInput): Promise<TrainingHost> {
  const publicSlug = normalizeTrainingHostSlug(input.publicSlug);
  if (!isValidTrainingHostSlug(publicSlug)) {
    throw new Error('주소는 2~40자의 영문·숫자·한글·하이픈만 사용할 수 있습니다.');
  }

  const hostRef = doc(db, COLLECTIONS.TRAINING_HOSTS, ownerUid);
  const existingHost = await getDoc(hostRef);
  const previousSlug = existingHost.exists()
    ? String(existingHost.data()?.publicSlug ?? '')
    : '';

  await assertSlugAvailable(publicSlug, ownerUid);

  const payload = {
    ownerUid,
    publicSlug,
    displayName: input.displayName.trim().slice(0, 80),
    bio: input.bio?.trim().slice(0, 500) || null,
    schoolName: input.schoolName?.trim().slice(0, 80) || null,
    photoURL: input.photoURL?.trim().slice(0, 2048) || null,
    isPublic: input.isPublic,
    updatedAt: serverTimestamp(),
    ...(existingHost.exists() ? {} : { createdAt: serverTimestamp() }),
  };

  const batch = writeBatch(db);
  batch.set(hostRef, payload, { merge: true });
  batch.set(slugDocRef(publicSlug), { ownerUid, updatedAt: serverTimestamp() }, { merge: true });

  if (previousSlug && previousSlug !== publicSlug) {
    const prevRef = slugDocRef(previousSlug);
    const prevSnap = await getDoc(prevRef);
    if (prevSnap.exists() && prevSnap.data()?.ownerUid === ownerUid) {
      batch.delete(prevRef);
    }
  }

  await batch.commit();

  const saved = await getDoc(hostRef);
  return mapHost(saved.id, saved.data() as Record<string, unknown>);
}

export async function listTrainingProgramsByOwner(ownerUid: string): Promise<TrainingProgram[]> {
  const q = query(programsCol(), where('ownerUid', '==', ownerUid));
  const snap = await getDocs(q);
  const programs = snap.docs.map((d) => mapProgram(d.id, d.data() as Record<string, unknown>));
  return sortTrainingProgramsByDateDesc(programs);
}

export async function getTrainingProgramById(programId: string): Promise<TrainingProgram | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.TRAINING_PROGRAMS, programId));
  if (!snap.exists()) return null;
  return mapProgram(snap.id, snap.data() as Record<string, unknown>);
}

export async function getPublicTrainingProgramBySlug(
  hostId: string,
  programSlug: string
): Promise<TrainingProgram | null> {
  const q = query(
    programsCol(),
    where('hostId', '==', hostId),
    where('programSlug', '==', programSlug),
    where('isPublic', '==', true)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return mapProgram(snap.docs[0].id, snap.docs[0].data() as Record<string, unknown>);
}

export async function getTrainingProgramBySlug(
  hostId: string,
  programSlug: string,
  viewerUid?: string
): Promise<TrainingProgram | null> {
  const publicProgram = await getPublicTrainingProgramBySlug(hostId, programSlug);
  if (publicProgram) return publicProgram;

  if (!viewerUid) return null;

  const q = query(
    programsCol(),
    where('ownerUid', '==', viewerUid),
    where('programSlug', '==', programSlug)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return mapProgram(snap.docs[0].id, snap.docs[0].data() as Record<string, unknown>);
}

async function assertProgramSlugAvailable(ownerUid: string, programSlug: string, programId: string): Promise<void> {
  const q = query(
    programsCol(),
    where('ownerUid', '==', ownerUid),
    where('programSlug', '==', programSlug)
  );
  const snap = await getDocs(q);
  const conflict = snap.docs.find((d) => d.id !== programId);
  if (conflict) {
    throw new Error('이 허브에서 이미 사용 중인 연수 주소입니다.');
  }
}

export async function addTrainingProgram(
  ownerUid: string,
  hostId: string,
  input: TrainingProgramInput
): Promise<string> {
  const contentType = input.contentType ?? (input.contentHtml ? 'editor' : input.path ? 'legacy' : 'editor');
  const programSlug =
    input.programSlug?.trim() ? normalizeProgramSlug(input.programSlug) : suggestProgramSlugFromTitle(input.title);

  if (contentType === 'editor' && !isValidProgramSlug(programSlug)) {
    throw new Error('연수 주소(slug) 형식을 확인해 주세요.');
  }

  const ref = doc(programsCol());
  const storedSlug =
    contentType === 'editor'
      ? programSlug
      : input.programSlug?.trim()
        ? normalizeProgramSlug(input.programSlug)
        : null;

  if (storedSlug) {
    if (!isValidProgramSlug(storedSlug)) {
      throw new Error('연수 주소(slug) 형식을 확인해 주세요.');
    }
    await assertProgramSlugAvailable(ownerUid, storedSlug, ref.id);
  }

  await setDoc(ref, {
    hostId,
    ownerUid,
    title: input.title.trim().slice(0, 120),
    description: input.description?.trim().slice(0, 500) || null,
    status: input.status,
    date: input.date || null,
    category: input.category?.trim().slice(0, 40) || null,
    tags: input.tags?.slice(0, 10).map((t) => t.trim().slice(0, 30)) || [],
    programSlug: storedSlug,
    contentType,
    contentHtml:
      contentType === 'editor'
        ? sanitizeTrainingHtml(input.contentHtml || DEFAULT_TRAINING_CONTENT_HTML)
        : null,
    path: input.path?.trim().slice(0, 500) || null,
    linkType: input.linkType ?? 'internal',
    isPublic: input.isPublic,
    sortOrder: input.sortOrder ?? Date.now(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteTrainingProgram(programId: string, ownerUid: string): Promise<void> {
  const ref = doc(db, COLLECTIONS.TRAINING_PROGRAMS, programId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data().ownerUid !== ownerUid) {
    throw new Error('삭제 권한이 없습니다.');
  }
  await deleteDoc(ref);
}

export async function updateTrainingProgram(
  programId: string,
  ownerUid: string,
  input: Partial<TrainingProgramInput>
): Promise<void> {
  const ref = doc(db, COLLECTIONS.TRAINING_PROGRAMS, programId);
  const snap = await getDoc(ref);
  if (!snap.exists() || snap.data().ownerUid !== ownerUid) {
    throw new Error('수정 권한이 없습니다.');
  }
  if (input.programSlug !== undefined) {
    const programSlug = normalizeProgramSlug(input.programSlug);
    if (!isValidProgramSlug(programSlug)) {
      throw new Error('연수 주소(slug) 형식을 확인해 주세요.');
    }
    await assertProgramSlugAvailable(ownerUid, programSlug, programId);
  }

  await updateDoc(ref, {
    ...(input.title !== undefined ? { title: input.title.trim().slice(0, 120) } : {}),
    ...(input.description !== undefined ? { description: input.description?.trim().slice(0, 500) || null } : {}),
    ...(input.status !== undefined ? { status: input.status } : {}),
    ...(input.date !== undefined ? { date: input.date || null } : {}),
    ...(input.category !== undefined ? { category: input.category?.trim().slice(0, 40) || null } : {}),
    ...(input.tags !== undefined ? { tags: input.tags?.slice(0, 10).map((t) => t.trim().slice(0, 30)) || [] } : {}),
    ...(input.programSlug !== undefined
      ? { programSlug: normalizeProgramSlug(input.programSlug) }
      : {}),
    ...(input.contentType !== undefined ? { contentType: input.contentType } : {}),
    ...(input.contentHtml !== undefined
      ? { contentHtml: sanitizeTrainingHtml(input.contentHtml) }
      : {}),
    ...(input.path !== undefined ? { path: input.path?.trim().slice(0, 500) || null } : {}),
    ...(input.linkType !== undefined ? { linkType: input.linkType } : {}),
    ...(input.isPublic !== undefined ? { isPublic: input.isPublic } : {}),
    ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
    updatedAt: serverTimestamp(),
  });
}

/** Firestore 인덱스·권한 오류를 사용자 친화 메시지로 변환 */
export function formatTrainingHostError(error: unknown): string {
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: string }).code) : '';
  const message = error instanceof Error ? error.message : String(error);

  if (code === 'permission-denied') {
    return 'Firestore 권한이 없습니다. 규칙 배포가 필요할 수 있습니다.';
  }
  if (code === 'failed-precondition' || message.includes('index')) {
    return 'Firestore 인덱스가 아직 생성 중입니다. 잠시 후 다시 시도해 주세요.';
  }
  return message || '요청 처리에 실패했습니다.';
}
